import { z } from 'zod';
import { notificationKinds } from './notifications';

// Field schemas of every synced collection: the API validates against them and the app uses them
// before writing locally. Amounts are integer cents (negative = money out). Dates are 'YYYY-MM-DD',
// instants are ISO 8601 UTC strings.

const MAX_CENTS = 100_000_000_000; // one billion euro

const name = z.string().trim().min(1, 'Inserisci un nome.').max(80, 'Il nome è troppo lungo.');
const notes = z.string().max(5000, 'Le note sono troppo lunghe.');
const isoDate = z.iso.date('Data non valida.');
const instant = z.iso.datetime({ message: 'Data e ora non valide.' });
const position = z.number().refine(Number.isFinite, 'Posizione non valida.');
const cents = z
  .number()
  .int('Importo non valido.')
  .min(-MAX_CENTS, 'Importo troppo grande.')
  .max(MAX_CENTS, 'Importo troppo grande.');
const positiveCents = cents.refine((value) => value > 0, "L'importo deve essere maggiore di zero.");
const reference = z.uuid();
const basisPoints = z.number().int('Percentuale non valida.').min(0).max(10_000);

/** Colours of labels and card covers (the tokens --label-<colour>). */
export const labelColors = [
  'green',
  'yellow',
  'orange',
  'red',
  'purple',
  'blue',
  'sky',
  'pink',
  'gray',
] as const;

/** A board groups lists, like "Personale" or "Clienti". */
const taskBoard = z.object({
  name,
  position,
  archivedAt: instant.nullable(),
});

const taskList = z.object({
  name,
  position,
  archivedAt: instant.nullable(),
  // Added later: lists stored before boards have none and belong to the first board.
  boardId: reference.nullable().optional(),
});

/** How often a goal starts again: every day, every week, or once. */
export const goalPeriods = ['day', 'week', 'total'] as const;

/**
 * A goal made of units to tick: "5 ore (da 60 minuti) ogni giorno", "30 pagine a settimana".
 * The units done are counted from the task's check-ins of the current period.
 */
const goal = z.object({
  units: z.number().int('Scrivi un numero intero.').min(1, 'Almeno 1.').max(200, 'Al massimo 200.'),
  /** Minutes of one unit when it is time (an hour, a pomodoro); null for pages, km… */
  unitMinutes: z.number().int().min(1).max(600).nullable(),
  /** The unit, plural: "ore", "pomodori", "pagine". */
  unit: z.string().trim().min(1, 'Scrivi l’unità.').max(24, 'L’unità è troppo lunga.'),
  period: z.enum(goalPeriods),
});

const checklistItem = z.object({
  id: z.string().min(1).max(64),
  text: z.string().trim().min(1, 'Scrivi l’elemento.').max(300, 'L’elemento è troppo lungo.'),
  done: z.boolean(),
});

const task = z.object({
  /** null = "In arrivo" (no list). */
  listId: reference.nullable(),
  title: z.string().trim().min(1, 'Scrivi cosa devi fare.').max(300, 'Il titolo è troppo lungo.'),
  notes,
  dueDate: isoDate.nullable(),
  /** 0 none, 1 low, 2 medium, 3 high. */
  priority: z.number().int().min(0).max(3),
  completedAt: instant.nullable(),
  position,
  // Added later: older records have none, stored as null (no labels, no checklist, no cover, not
  // archived).
  labelIds: z.array(reference).max(20, 'Troppe etichette.').nullable().optional(),
  checklist: z.array(checklistItem).max(200, 'La checklist è troppo lunga.').nullable().optional(),
  cover: z.enum(labelColors).nullable().optional(),
  archivedAt: instant.nullable().optional(),
  goal: goal.nullable().optional(),
  /** How long you think it takes, in minutes: the ground for planning the day. */
  estimateMinutes: z.number().int().min(5).max(1440).nullable().optional(),
});

/** One step towards a task's goal: units ticked at a moment, by hand or by a finished session. */
const taskCheckin = z.object({
  taskId: reference,
  at: instant,
  units: z.number().int().min(1).max(200),
  /** The focus session that produced it, when it came from the timer. */
  sessionId: reference.nullable(),
});

/** A label you can put on cards: a colour and, if you want, a name. */
const taskLabel = z.object({
  name: z.string().trim().max(40, 'Il nome è troppo lungo.'),
  color: z.enum(labelColors),
  position,
});

/** A comment on a card. */
const taskComment = z.object({
  taskId: reference,
  body: z.string().trim().min(1, 'Scrivi un commento.').max(5000, 'Il commento è troppo lungo.'),
});

/**
 * "investment" counts in your wealth; "external" is money that is not yours (parents, a shared fund,
 * a gift): what it pays is part of what you cost, but never touches your balances.
 */
export const moneyAccountKinds = [
  'bank',
  'cash',
  'savings',
  'card',
  'investment',
  'external',
] as const;
const moneyAccount = z.object({
  name,
  kind: z.enum(moneyAccountKinds),
  openingBalance: cents,
  position,
  archivedAt: instant.nullable(),
});

export const categoryKinds = ['expense', 'income'] as const;
const category = z.object({
  name,
  kind: z.enum(categoryKinds),
  position,
  archivedAt: instant.nullable(),
  // Added later: records stored on a device before then do not have them (read as null / false).
  /** The category right above (three levels at most: Cena › Cena fuori › Sushi). */
  parentId: reference.nullable().optional(),
  /** Business expense: counted apart from personal spending. */
  business: z.boolean().optional(),
});

/** How a movement was paid or received; the account says whose money it was. */
export const paymentMethods = ['cash', 'card', 'transfer'] as const;

/** What a recurrence repeats, and how often. */
export const recurrenceKinds = ['transaction', 'task', 'event'] as const;
export const recurrenceFrequencies = ['daily', 'weekly', 'monthly', 'yearly'] as const;

/** What starts a rule, and what it does. */
export const ruleTriggers = ['invoicePaid', 'incomeReceived', 'taskCompleted'] as const;
export const ruleActions = ['setAside', 'draftInvoice'] as const;

/** Numbers you set yourself for the week, on top of the drawn challenges. */
export const weeklyGoalMetrics = [
  'focusMinutes',
  'sessions',
  'tasks',
  'blocksKept',
  'recordedDays',
] as const;

/** Events made in the app, or imported from Google Calendar. */
export const eventSources = ['app', 'google'] as const;

const transaction = z.object({
  accountId: reference,
  categoryId: reference.nullable(),
  amount: cents.refine((value) => value !== 0, "L'importo non può essere zero."),
  occurredOn: isoDate,
  note: z.string().trim().max(500, 'La nota è troppo lunga.'),
  // Added later: older records have no method (read as "not given").
  method: z.enum(paymentMethods).nullable().optional(),
  /** Local time of day, "HH:MM": morning, afternoon or evening. Older records have none. */
  time: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Ora non valida.')
    .nullable()
    .optional(),
});

const budget = z.object({
  categoryId: reference,
  monthlyLimit: positiveCents,
});

const savingsGoal = z.object({
  name,
  target: positiveCents,
  saved: cents.refine((value) => value >= 0, 'Il risparmio non può essere negativo.'),
  deadline: isoDate.nullable(),
  position,
  archivedAt: instant.nullable(),
});

const nonNegativeCents = cents.refine((value) => value >= 0, "L'importo non può essere negativo.");

/**
 * A car, a motorbike: what it costs to use, per kilometre. Estimates only: they stay here and never
 * become movements, so balances and budgets go on showing the money that really left.
 */
const vehicle = z.object({
  name,
  /** Its expenses are the movements in this category and in those below it. */
  categoryId: reference.nullable(),
  /** Movements here come out of its fund: the mechanic, the tyres. */
  maintenanceCategoryId: reference.nullable(),
  /** The savings goal that holds its fund. */
  goalId: reference.nullable(),
  /** Counted from this day: earlier expenses are not part of the sums. */
  startOn: isoDate,
  /** The odometer on that day. */
  startKm: z
    .number()
    .int('Scrivi un numero intero.')
    .min(0, 'Non può essere negativo.')
    .max(10_000_000),
  annualKm: z
    .number()
    .int('Scrivi un numero intero.')
    .min(1, 'Scrivi quanti km fai in un anno.')
    .max(1_000_000, 'Troppi km.'),
  /** Kilometres with a litre (or a kWh), and what that costs; 0 when not given. */
  kmPerUnit: z.number().min(0, 'Non può essere negativo.').max(1000, 'Troppi km.'),
  fuelPrice: nonNegativeCents,
  /** A year of servicing, tyres, repairs. */
  maintenancePerYear: nonNegativeCents,
  /** A year of insurance, road tax, garage: what you pay even with the car standing still. */
  fixedPerYear: nonNegativeCents,
  /** What it is worth today, what it will be worth, and how many years you keep it. */
  currentValue: nonNegativeCents,
  resaleValue: nonNegativeCents,
  yearsLeft: z
    .number()
    .int('Scrivi un numero intero.')
    .min(1, 'Almeno 1 anno.')
    .max(50, 'Troppi anni.'),
  position,
  archivedAt: instant.nullable(),
});

/** Kilometres driven on a day. */
const vehicleTrip = z.object({
  vehicleId: reference,
  day: isoDate,
  km: z
    .number()
    .int('Scrivi un numero intero.')
    .min(1, 'Scrivi i km percorsi.')
    .max(100_000, 'Troppi km.'),
  note: z.string().trim().max(200, 'La nota è troppo lunga.'),
});

const year = z
  .number()
  .int('Anno non valido.')
  .min(2000, 'Anno non valido.')
  .max(2100, 'Anno non valido.');
const optionalUrl = z
  .string()
  .trim()
  .max(1000, 'Il link è troppo lungo.')
  .refine(
    (value) => value === '' || /^https?:\/\/\S+$/.test(value),
    'Il link deve iniziare con https://.',
  );

/** An invoice you issued. Once paid it points to the income it produced in your accounts. */
const invoice = z.object({
  client: z.string().trim().min(1, 'Scrivi il cliente.').max(160, 'Il nome è troppo lungo.'),
  subject: z.string().trim().max(300, "L'oggetto è troppo lungo."),
  issuedOn: isoDate,
  amount: positiveCents,
  paidOn: isoDate.nullable(),
  transactionId: reference.nullable(),
  // Added later: older invoices have none.
  /** When the client should pay: after it, the invoice is overdue. */
  dueOn: isoDate.nullable().optional(),
  /** Made by a rule and still to check: not counted until you confirm it. */
  draft: z.boolean().nullable().optional(),
});

export const plannedIncomeKinds = ['quote', 'contract', 'grant', 'other'] as const;
/** Money you expect in a year but have not invoiced yet: quotes, contracts, grants. */
const plannedIncome = z.object({
  name,
  kind: z.enum(plannedIncomeKinds),
  amount: positiveCents,
  year,
  note: z.string().trim().max(500, 'La nota è troppo lunga.'),
});

export const obligationDirections = ['payable', 'receivable'] as const;
/**
 * Money you owe (taxes, a loan) or are owed (a refund), settled once. Once settled it points to the
 * movement it produced in your accounts.
 */
const obligation = z.object({
  name,
  direction: z.enum(obligationDirections),
  amount: positiveCents,
  dueOn: isoDate.nullable(),
  settledOn: isoDate.nullable(),
  note: z.string().trim().max(500, 'La nota è troppo lunga.'),
  // Added later: older records have none (settled without a movement).
  transactionId: reference.nullable().optional(),
});

/** Something you plan to buy in a year. Once bought it points to the expense it produced. */
const plannedPurchase = z.object({
  name: z.string().trim().min(1, 'Scrivi cosa vuoi comprare.').max(160, 'Il nome è troppo lungo.'),
  url: optionalUrl,
  price: positiveCents,
  necessary: z.boolean(),
  business: z.boolean(),
  year,
  note: z.string().trim().max(500, 'La nota è troppo lunga.'),
  purchasedOn: isoDate.nullable(),
  // Added later: older records have none (read as null).
  categoryId: reference.nullable().optional(),
  /** When you plan to buy it. */
  plannedOn: isoDate.nullable().optional(),
  transactionId: reference.nullable().optional(),
});

/** Settings of a tax year: the share of income set aside for taxes and contributions. */
const fiscalYear = z.object({
  year,
  /** Basis points: 2000 = 20%. */
  taxRate: z
    .number()
    .int('Percentuale non valida.')
    .min(0, 'La percentuale va da 0 a 100.')
    .max(10_000, 'La percentuale va da 0 a 100.'),
  // Added later, for the flat-rate regime (forfettario): older years have none.
  forfettario: z.boolean().nullable().optional(),
  /** Coefficiente di redditività, basis points: 7800 = 78%. */
  profitability: basisPoints.nullable().optional(),
  /** Imposta sostitutiva, basis points: 500 or 1500. */
  substituteRate: basisPoints.nullable().optional(),
  /** Contributi INPS on the taxable income, basis points: 2607 = 26.07%. */
  pensionRate: basisPoints.nullable().optional(),
});

const recurrenceTemplate = z.object({
  // Movements
  accountId: reference.optional(),
  categoryId: reference.nullable().optional(),
  amount: cents.refine((value) => value !== 0, "L'importo non può essere zero.").optional(),
  note: z.string().trim().max(500, 'La nota è troppo lunga.').optional(),
  method: z.enum(paymentMethods).nullable().optional(),
  time: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Ora non valida.')
    .nullable()
    .optional(),
  // Tasks and events
  title: z.string().trim().max(300, 'Il titolo è troppo lungo.').optional(),
  notes: notes.optional(),
  listId: reference.nullable().optional(),
  priority: z.number().int().min(0).max(3).optional(),
  labelIds: z.array(reference).max(20).nullable().optional(),
  /** Every copy starts with these items, unticked. */
  checklist: z.array(checklistItem).max(200).nullable().optional(),
  allDay: z.boolean().optional(),
  minutes: z.number().int().min(5).max(1440).optional(),
  taskId: reference.nullable().optional(),
});

const recurrence = z.object({
  kind: z.enum(recurrenceKinds),
  /** The fields of every copy; the date comes from the rule. */
  template: recurrenceTemplate,
  frequency: z.enum(recurrenceFrequencies),
  /** Every how many days, weeks, months or years. */
  interval: z.number().int().min(1, 'Ogni quanto?').max(52, 'Troppo distante.'),
  startOn: isoDate,
  endOn: isoDate.nullable(),
  /** Copies already made up to this day: a deleted copy is not made again. */
  generatedUntil: isoDate.nullable(),
  paused: z.boolean(),
});

const ruleAllocation = z.object({
  goalId: reference,
  /** Basis points of the amount: 2500 = 25%. */
  share: z.number().int().min(1).max(10_000),
});

const rule = z.object({
  name,
  trigger: z.enum(ruleTriggers),
  /** Which events count: income of a category, cards with a label, at least an amount. */
  filter: z.object({
    categoryId: reference.nullable().optional(),
    labelId: reference.nullable().optional(),
    minAmount: cents.nullable().optional(),
  }),
  action: z.enum(ruleActions),
  params: z.object({
    allocations: z.array(ruleAllocation).max(10).optional(),
    /** For a draft invoice: who and how much, when the card does not say it. */
    client: z.string().trim().max(160).optional(),
    amount: cents.nullable().optional(),
  }),
  enabled: z.boolean(),
  position,
});

/** One rule applied to one event: made once, whichever device sees it first. */
const ruleRun = z.object({
  ruleId: reference,
  sourceId: reference,
  at: instant,
  summary: z.string().max(300),
});

const weeklyGoal = z.object({
  id: z.string().min(1).max(64),
  metric: z.enum(weeklyGoalMetrics),
  target: z.number().int().min(1).max(100_000),
  /** Monday of the first week it counts for. */
  since: isoDate,
});

/** Your choices that every device must agree on (one record per user). */
const preferences = z.object({
  /** 0 = Monday … 6 = Sunday: days off that do not break a streak. */
  restWeekdays: z.array(z.number().int().min(0).max(6)).max(3),
  /** Single days off. */
  restDays: z.array(isoDate).max(400),
  weeklyGoals: z.array(weeklyGoal).max(5),
  /** Mondays of the weeks reviewed. */
  reviewedWeeks: z.array(isoDate).max(400),
  // Added later: older records have none (no notifications).
  /** Whether a device asked for notifications, and the kinds you switched off. */
  notifications: z
    .object({
      enabled: z.boolean(),
      off: z.array(z.enum(notificationKinds)).max(notificationKinds.length),
    })
    .nullable()
    .optional(),
});

export const challengePeriods = ['day', 'week'] as const;
/** What fills a challenge of yours: a number the app measures, or a tick you give yourself. */
export const challengeMetrics = [
  'manual',
  'focusMinutes',
  'sessions',
  'tasks',
  'onTime',
  'units',
  'blocksKept',
  'recordedDays',
] as const;

/** A challenge you set yourself, every day or every week, with its own points. */
const challenge = z.object({
  title: z.string().trim().min(1, 'Dai un nome alla sfida.').max(80, 'Il nome è troppo lungo.'),
  period: z.enum(challengePeriods),
  metric: z.enum(challengeMetrics),
  target: z.number().int().min(1, 'Almeno 1.').max(100_000, 'Troppo.'),
  points: z.number().int().min(5).max(200),
  /** For daily ones, the days it applies (0 = Monday … 6 = Sunday); null = every day. */
  weekdays: z.array(z.number().int().min(0).max(6)).max(7).nullable(),
  /** Days ticked by hand, for the challenges you tick yourself. */
  doneDays: z.array(isoDate).max(400),
  /** First day it counts (a Monday for weekly ones). */
  since: isoDate,
  archivedAt: instant.nullable(),
});

export const objectiveSections = ['attivita', 'finanze', 'incassi'] as const;
/** The indicators an objective can follow; the app measures each one from your data. */
export const objectiveMetrics = [
  // Finanze
  'net',
  'income',
  'monthlySpending',
  'savingsRate',
  'balance',
  'savingsGoal',
  // Incassi
  'invoiced',
  'collected',
  'invoiceCount',
  'clients',
  'averageInvoice',
  'outstanding',
  'topClientShare',
  // Attività
  'tasksDone',
  'focusMinutes',
  'onTimeRate',
  'boardProgress',
  'blocksKept',
  // Any section
  'steps',
] as const;

const objectiveKpi = z.object({
  id: z.string().min(1).max(64),
  metric: z.enum(objectiveMetrics),
  /** Cents for money, minutes for time, a count, or a percentage. */
  target: z.number().int().min(0).max(10_000_000_000_000),
  /** What it is about, when it can be narrowed: a board, a label, a category, a client… */
  scope: z.string().max(200).nullable(),
});

/** Something you want to reach, followed by indicators you choose. */
const objective = z.object({
  section: z.enum(objectiveSections),
  title: z.string().trim().min(1, 'Dai un nome all’obiettivo.').max(120, 'Il nome è troppo lungo.'),
  note: z.string().trim().max(1000, 'La nota è troppo lunga.'),
  startsOn: isoDate,
  dueOn: isoDate.nullable(),
  kpis: z.array(objectiveKpi).max(8, 'Al massimo 8 indicatori.'),
  /** The steps to get there, ticked by hand. */
  steps: z.array(checklistItem).max(50, 'Al massimo 50 passi.'),
  position,
  doneAt: instant.nullable(),
  archivedAt: instant.nullable(),
});

const event = z.object({
  title: z.string().trim().min(1, "Dai un nome all'evento.").max(200, 'Il titolo è troppo lungo.'),
  startsAt: instant,
  endsAt: instant,
  allDay: z.boolean(),
  notes,
  // Added later: a block of time for a task (planned work). Older events have none.
  taskId: reference.nullable().optional(),
  /** Where the event comes from: made here, or imported from Google Calendar (read-only). */
  source: z.enum(eventSources).nullable().optional(),
  /** Google: "calendar|event" of an imported event, or the id of the copy sent to Google. */
  externalId: z.string().max(400).nullable().optional(),
  /** The Google calendar an imported event belongs to. */
  calendar: z.string().max(200).nullable().optional(),
});

const focusSession = z.object({
  taskId: reference.nullable(),
  label: z.string().trim().max(120, 'Il nome è troppo lungo.'),
  startedAt: instant,
  plannedMinutes: z.number().int().min(1).max(240),
  endedAt: instant.nullable(),
  completed: z.boolean(),
});

/** Shape of the editable fields of each collection. */
export const collectionFields = {
  taskBoards: taskBoard,
  taskLists: taskList,
  tasks: task,
  taskLabels: taskLabel,
  taskComments: taskComment,
  taskCheckins: taskCheckin,
  moneyAccounts: moneyAccount,
  categories: category,
  transactions: transaction,
  budgets: budget,
  savingsGoals: savingsGoal,
  vehicles: vehicle,
  vehicleTrips: vehicleTrip,
  events: event,
  focusSessions: focusSession,
  invoices: invoice,
  plannedIncomes: plannedIncome,
  obligations: obligation,
  plannedPurchases: plannedPurchase,
  fiscalYears: fiscalYear,
  recurrences: recurrence,
  rules: rule,
  ruleRuns: ruleRun,
  preferences,
  challenges: challenge,
  objectives: objective,
} as const;

export type CollectionName = keyof typeof collectionFields;
export const collectionNames = Object.keys(collectionFields) as CollectionName[];
export const collectionNameSchema = z.enum(
  collectionNames as [CollectionName, ...CollectionName[]],
);

export type CollectionFields = {
  [C in CollectionName]: z.output<(typeof collectionFields)[C]>;
};

/** Rules across fields, checked on the complete record. */
const crossFieldRules: Partial<
  Record<CollectionName, (record: never, ctx: z.RefinementCtx) => void>
> = {
  events: (record: CollectionFields['events'], ctx) => {
    if (record.endsAt < record.startsAt) {
      ctx.addIssue({
        code: 'custom',
        path: ['endsAt'],
        message: "La fine non può essere prima dell'inizio.",
      });
    }
  },
};

/** Validates a complete record (after merging an update into the stored one). */
export function validateRecord<C extends CollectionName>(collection: C, value: unknown) {
  const rule = crossFieldRules[collection];
  const schema = collectionFields[collection] as unknown as z.ZodType<CollectionFields[C]>;
  return (rule ? schema.superRefine(rule as never) : schema).safeParse(value);
}

/** Validates the fields of an update, each optional. */
export function validatePatch<C extends CollectionName>(collection: C, value: unknown) {
  return collectionFields[collection].partial().safeParse(value) as z.ZodSafeParseResult<
    Partial<CollectionFields[C]>
  >;
}

/** Sync metadata that comes with every record on the wire. */
export type RecordMeta = {
  id: string;
  version: number;
  createdAt: string;
  updatedAt: string;
  /** Set when the record was deleted: deletions travel as "tombstones". */
  deletedAt: string | null;
};

export type SyncedRecord<C extends CollectionName> = RecordMeta & CollectionFields[C];
